import Footer from "@/components/Footer";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import { getImageProps } from "@/lib/image-utils";
import { useParams } from "react-router-dom";

const caseStudies = {
  "cnc-lathe-optimization": {
    title: "CNC Lathe Drive Alarm Troubleshooting",
    titleCN: "CNC车床驱动报警故障排查",
    date: "CNC Lathe",
    readTime: "Service example",
    machine: "CNC Lathe",
    machineCN: "CNC车床",
    image: "/images/case-studies/cnc-lathe.jpg",
    content: {
      challenge: {
        en: "A CNC lathe was stopping during production with a recurring drive alarm. The customer needed the fault checked before the machine could be returned to regular work.",
        cn: "一台CNC车床在生产过程中反复出现驱动报警并停机。客户需要先排查故障，确认机床能否恢复正常使用。"
      },
      solution: {
        en: "We checked the alarm history, inspected the drive and wiring condition, reviewed related machine parameters, and tested the machine through basic movement and spindle operation.",
        cn: "我们检查报警记录、驱动器和线路状态，确认相关机床参数，并通过基本轴向移动和主轴运行测试机床状态。"
      },
      results: {
        en: "The fault path was narrowed down and the customer received practical repair recommendations before restarting production. We also advised checks to watch for if the alarm returns.",
        cn: "故障范围已缩小，客户在恢复生产前获得了实际维修建议。我们也说明了后续若报警再次出现时应重点检查的项目。"
      }
    }
  },
  "5-axis-mill-enhancement": {
    title: "5-Axis CNC Mill Preventive Maintenance",
    titleCN: "五轴CNC铣床预防性保养",
    date: "5-Axis Mill",
    readTime: "Service example",
    machine: "5-Axis CNC Mill",
    machineCN: "五轴CNC铣床",
    image: "/images/case-studies/5-axis-mill.jpg",
    content: {
      challenge: {
        en: "A workshop wanted a condition check on a 5-axis CNC mill before heavier production. The goal was to catch small issues early and reduce the chance of unexpected downtime.",
        cn: "一家车间希望在较重生产安排前，对五轴CNC铣床进行状态检查，尽早发现小问题，降低突然停机的风险。"
      },
      solution: {
        en: "The service included visual inspection, lubrication checks, axis movement checks, basic alignment review, and a discussion with the operator about recent machine behavior.",
        cn: "服务内容包括外观检查、润滑检查、轴向移动检查、基本精度确认，并与操作员了解近期机床运行状况。"
      },
      results: {
        en: "The customer received a clear maintenance summary and a list of items to monitor, helping them plan service before small issues become urgent breakdowns.",
        cn: "客户获得清楚的保养总结和后续需留意的项目，方便在小问题发展成紧急故障前安排处理。"
      }
    }
  },
  "edm-precision": {
    title: "EDM Machine Calibration & Service",
    titleCN: "电火花机校准与保养",
    date: "EDM Machine",
    readTime: "Service example",
    machine: "EDM Machine",
    machineCN: "电火花加工机",
    image: "/images/case-studies/edm-machine.jpg",
    content: {
      challenge: {
        en: "An EDM machine was producing inconsistent results, and the shop wanted the machine checked before continuing with mold work.",
        cn: "一台电火花机加工结果不够稳定，车间希望在继续模具加工前先检查机床状态。"
      },
      solution: {
        en: "We inspected the machine condition, checked calibration-related points, reviewed consumables and setup condition, and adjusted where needed.",
        cn: "我们检查机床状态、校准相关项目、耗材和装夹情况，并根据需要进行调整。"
      },
      results: {
        en: "The machine was serviced and the customer received practical guidance on setup checks and maintenance items that affect cutting consistency.",
        cn: "机床完成保养后，客户也获得关于装夹检查和影响加工稳定性的保养项目建议。"
      }
    }
  },
  "wire-cut-optimization": {
    title: "Wire Cut EDM Alignment Check",
    titleCN: "线切割电火花机校正检查",
    date: "Wire Cut EDM",
    readTime: "Service example",
    machine: "Wire Cut EDM",
    machineCN: "线切割电火花机",
    image: "/images/case-studies/wire-cut.jpg",
    content: {
      challenge: {
        en: "A wire cut EDM machine needed checking after the operator noticed unstable cutting behavior and inconsistent finish.",
        cn: "操作员发现线切割电火花机切割状态不稳定、表面效果不一致，因此需要进行检查。"
      },
      solution: {
        en: "The service covered alignment checks, wire path inspection, consumable review, flushing condition, and basic cutting stability checks.",
        cn: "服务内容包括校正检查、线路路径检查、耗材确认、冲水状态检查和基本切割稳定性测试。"
      },
      results: {
        en: "The operator received a clearer picture of the machine condition and recommended checks to keep cutting behavior more stable.",
        cn: "操作员更清楚了解机床状态，并获得保持切割稳定性的检查建议。"
      }
    }
  },
  "grinding-machine": {
    title: "Surface Grinding Machine Retrofit",
    titleCN: "平面磨床改造升级",
    date: "Surface Grinder",
    readTime: "Service example",
    machine: "Surface Grinder",
    machineCN: "平面磨床",
    image: "/images/case-studies/surface-grinder.jpg",
    content: {
      challenge: {
        en: "An older surface grinding machine was becoming harder to operate reliably, and the customer wanted to improve day-to-day usability without replacing the whole machine.",
        cn: "一台老旧平面磨床日常使用稳定性下降，客户希望在不更换整机的情况下提升使用便利性。"
      },
      solution: {
        en: "We reviewed the machine condition, checked control and mechanical items, and carried out retrofit and adjustment work suited to the customer's operating needs.",
        cn: "我们检查机床状态、控制与机械部分，并根据客户日常操作需求进行改造与调整。"
      },
      results: {
        en: "The machine was returned with practical improvements focused on reliability, easier operation, and future serviceability.",
        cn: "机床完成处理后，重点提升了稳定性、操作便利性和后续维修便利性。"
      }
    }
  },
  "machining-center": {
    title: "Vertical Machining Center Service Visit",
    titleCN: "立式加工中心维修服务",
    date: "VMC",
    readTime: "Service example",
    machine: "VMC",
    machineCN: "立式加工中心",
    image: "/images/case-studies/vmc.jpg",
    content: {
      challenge: {
        en: "A vertical machining center needed a service visit after the customer noticed changes in machine behavior during regular work.",
        cn: "客户在日常加工中发现立式加工中心运行状态有所变化，因此安排维修检查。"
      },
      solution: {
        en: "We checked the machine condition, reviewed recent alarms or symptoms with the operator, and performed maintenance checks around movement, spindle behavior, and basic machine operation.",
        cn: "我们检查机床状态，与操作员确认近期报警或异常现象，并围绕轴向移动、主轴状态和基本运行进行保养检查。"
      },
      results: {
        en: "The customer received a clear service summary and practical next steps for keeping the machine stable in regular production.",
        cn: "客户获得清楚的维修总结，以及保持机床在日常生产中稳定运行的后续建议。"
      }
    }
  }
};

const SITE_URL = "https://wiiseng.vercel.app";

const CaseStudy = () => {
  const { id } = useParams();
  const caseStudy = caseStudies[id as keyof typeof caseStudies];

  if (!caseStudy) {
    return <div>Case study not found</div>;
  }

  const imageProps = getImageProps("/og-image.png", caseStudy.title);
  const pageUrl = `${SITE_URL}/case-studies/${id}`;
  const pageTitle = `${caseStudy.title} | Wii Seng Technology Malaysia`;
  const pageDescription = caseStudy.content.challenge.en;
  const imageUrl = `${SITE_URL}/og-image.png`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": caseStudy.title,
    "description": pageDescription,
    "image": imageUrl,
    "inLanguage": ["en", "zh"],
    "url": pageUrl,
    "mainEntityOfPage": pageUrl,
    "datePublished": "2026-06-02",
    "dateModified": "2026-06-02",
    "articleSection": "CNC Repair Notes",
    "author": {
      "@type": "Organization",
      "name": "Wii Seng Technology Resources",
      "url": SITE_URL,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Wii Seng Technology Resources",
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/favicon.ico`,
      },
    },
    "about": caseStudy.machine,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Repair Notes",
        "item": `${SITE_URL}/case-studies`,
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": caseStudy.title,
        "item": pageUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" hrefLang="en-MY" href={pageUrl} />
        <link rel="alternate" hrefLang="zh" href={pageUrl} />
        <link rel="alternate" hrefLang="x-default" href={pageUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Wii Seng Technology Resources" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={pageTitle} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:image:alt" content={pageTitle} />

        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <Navbar />
      <main className="flex-grow">
        <article className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <header className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm font-medium text-wiiseng-blue">{caseStudy.machine}</span>
                <span className="text-sm text-gray-500">•</span>
                <span className="text-sm text-gray-500">{caseStudy.date}</span>
                <span className="text-sm text-gray-500">•</span>
                <span className="text-sm text-gray-500">{caseStudy.readTime}</span>
              </div>
              <h1 className="text-4xl font-bold mb-2 text-wiiseng-navy">
                {caseStudy.title}
              </h1>
              <p
                className="text-2xl font-semibold mb-8 text-wiiseng-navy/80"
                lang="zh"
              >
                {caseStudy.titleCN}
              </p>
              <img
                {...imageProps}
                width={1200}
                height={630}
                loading="lazy"
                className="w-full h-[400px] object-cover rounded-lg mb-8"
              />
            </header>

            <div className="prose prose-lg max-w-none">
              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-1 text-wiiseng-navy">
                  The Challenge
                </h2>
                <p
                  className="text-lg font-semibold mb-4 text-wiiseng-navy/70"
                  lang="zh"
                >
                  挑战
                </p>
                <p className="mb-4">{caseStudy.content.challenge.en}</p>
                <p className="mb-4" lang="zh">{caseStudy.content.challenge.cn}</p>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-1 text-wiiseng-navy">
                  Our Solution
                </h2>
                <p
                  className="text-lg font-semibold mb-4 text-wiiseng-navy/70"
                  lang="zh"
                >
                  解决方案
                </p>
                <div className="whitespace-pre-line mb-4">{caseStudy.content.solution.en}</div>
                <div className="whitespace-pre-line mb-4" lang="zh">{caseStudy.content.solution.cn}</div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-1 text-wiiseng-navy">
                  Results
                </h2>
                <p
                  className="text-lg font-semibold mb-4 text-wiiseng-navy/70"
                  lang="zh"
                >
                  成果
                </p>
                <div className="whitespace-pre-line mb-4">{caseStudy.content.results.en}</div>
                <div className="whitespace-pre-line mb-4" lang="zh">{caseStudy.content.results.cn}</div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default CaseStudy; 